/**
 * Which garage the user is looking at, and what they may do in it.
 *
 * The garage is the tenant (§2), so almost every query and every permission
 * question starts here. Two things this file is careful about:
 *
 *  - **The role comes from the server.** `GET /garages` returns each garage with
 *    the caller's `role` in it, resolved from their membership. The UI never
 *    infers a role, never stores one, and never trusts the ID token's claims for
 *    it. This context is a cache of the API's answer, not a second opinion.
 *  - **The permissions below mirror the API's routes exactly** — they are read
 *    off apps/api/src/routes/*, where each handler declares its minimum role.
 *    The API is authoritative and will 403 regardless; these exist so the UI
 *    never shows a button that is going to 403 (§6). If the two ever disagree,
 *    this file is the one that is wrong.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { GarageDto, Role } from '@svcdue/contracts';

import { useGarages } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';

/**
 * The last garage picked, remembered across visits.
 *
 * localStorage is fine for this and only this: a garage id is not a credential,
 * it is a UI preference. It is also validated against the server's list on every
 * load below, so a stale or hand-edited value selects nothing rather than
 * anything.
 */
const SELECTED_KEY = 'svcdue.garageId';

interface GarageState {
  garages: GarageDto[];
  garage: GarageDto | undefined;
  garageId: string | undefined;
  role: Role | undefined;
  select: (garageId: string) => void;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  /** True when the user has no garage at all — the create-garage prompt. */
  isEmpty: boolean;
}

/**
 * Exported so a test can supply a role directly.
 *
 * Role-gating is the thing most worth testing on this screen (§6), and driving
 * it through the provider would mean standing up a query client, a fake
 * `GET /garages` and a signed-in session just to answer "does crew see the Add
 * Part button". Rendering against the context the components actually read is
 * the same question with none of that in the way.
 */
export const GarageContext = createContext<GarageState | null>(null);
export type { GarageState };

export function GarageProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useAuth();
  const query = useGarages(isSignedIn);
  const [selectedId, setSelectedId] = useState<string | undefined>(() => readStored());

  const garages = useMemo(() => query.data ?? [], [query.data]);

  /**
   * Resolve the selection against what the server actually returned.
   *
   * A remembered id can be gone — the garage was deleted, or the user was
   * removed from it, or this is a different account on a shared browser. In all
   * three the id is meaningless and must not be trusted just because it is in
   * storage. Falling back to the first garage is what makes "removed from a
   * garage overnight" land on a working screen instead of an endless spinner.
   */
  const garage = useMemo(
    () => garages.find((g) => g.id === selectedId) ?? garages[0],
    [garages, selectedId],
  );

  useEffect(() => {
    if (garage) window.localStorage.setItem(SELECTED_KEY, garage.id);
  }, [garage]);

  const value = useMemo<GarageState>(
    () => ({
      garages,
      garage,
      garageId: garage?.id,
      role: garage?.role,
      select: (id: string) => setSelectedId(id),
      isPending: query.isPending,
      isError: query.isError,
      error: query.error,
      refetch: () => void query.refetch(),
      isEmpty: !query.isPending && !query.isError && garages.length === 0,
    }),
    [garages, garage, query.isPending, query.isError, query.error, query.refetch],
  );

  return <GarageContext.Provider value={value}>{children}</GarageContext.Provider>;
}

export function useGarageContext(): GarageState {
  const context = useContext(GarageContext);
  if (!context) throw new Error('useGarageContext() used outside <GarageProvider>');
  return context;
}

function readStored(): string | undefined {
  try {
    return window.localStorage.getItem(SELECTED_KEY) ?? undefined;
  } catch {
    // Storage disabled. The provider falls back to the first garage, which is
    // a fine default — it just will not be remembered.
    return undefined;
  }
}

// --- permissions: a mirror of the API's role ladder ----------------------

/**
 * Structural changes — what the fleet *is*. Owner only, everywhere:
 * cars, drivers and items are `requireMembership(..., 'owner')` on every write
 * (routes/fleet.ts, routes/items.ts).
 */
export const canManageFleet = (role: Role | undefined): boolean => role === 'owner';

/**
 * Operational actions — what happened this weekend. Owner *or* crew: service
 * logs, "mark checked" and events are `requireMembership(..., 'crew')`
 * (routes/items.ts, routes/events.ts). This is the entire reason the crew role
 * exists (§2), so it is the one thing crew must always see.
 */
export const canLogMaintenance = (role: Role | undefined): boolean => role === 'owner' || role === 'crew';

/** The member list is other people's names and emails — owner only (§11). */
export const canManageMembers = (role: Role | undefined): boolean => role === 'owner';

/** Sharing is revocable only by the owner, so only the owner may enable it. */
export const canManageSharing = (role: Role | undefined): boolean => role === 'owner';

/** Convenience for the screens, so they never re-derive the rules above. */
export function usePermissions() {
  const { role } = useGarageContext();
  return useMemo(
    () => ({
      role,
      canManageFleet: canManageFleet(role),
      canLogMaintenance: canLogMaintenance(role),
      canManageMembers: canManageMembers(role),
      canManageSharing: canManageSharing(role),
    }),
    [role],
  );
}
